import {
  Box,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { ProgramActionsMenu } from "../../program-move/components/program-actions-menu";
import { describeNext, updatedShortText } from "../program-landing";
import {
  ABOVE_LINK,
  AccessPill,
  FavouriteButton,
  MatchBadge,
  ProgramBadge,
  ProgramTitleLink,
  visuallyHidden,
} from "./program-parts";

import type { AccessData } from "../../program-access/hooks/use-program-access";
import type { ProgramTree } from "../../program-tree/program-tree";
import type { ProgramMatch, ProgramSort } from "../program-landing";
import type { FC } from "react";

/** The columns after the program's, with the sort that a click on the header picks. */
const columns: {
  label: string;
  sort?: ProgramSort;
  direction?: "asc" | "desc";
  numeric?: boolean;
  width: number;
}[] = [
  { label: "Folders", numeric: true, width: 80 },
  { label: "Communications", sort: "communications", direction: "desc", numeric: true, width: 140 },
  { label: "Active", numeric: true, width: 80 },
  { label: "Drafts", sort: "drafts", direction: "desc", numeric: true, width: 90 },
  { label: "Next send", sort: "next", direction: "asc", width: 240 },
  { label: "Access", width: 150 },
  { label: "Updated", sort: "updated", direction: "asc", width: 120 },
];

const headSx = {
  height: 44,
  py: 0,
  typography: "overline",
  lineHeight: 1,
  color: "text.secondary",
  whiteSpace: "nowrap",
} as const;

const SortHeader: FC<{
  label: string;
  value: ProgramSort;
  direction: "asc" | "desc";
  sort: ProgramSort;
  onSort: (sort: ProgramSort) => void;
}> = ({ label, value, direction, sort, onSort }) => (
  <TableSortLabel
    active={sort === value}
    direction={direction}
    onClick={() => onSort(value)}
    data-analytics-id={`programs-landing-sort-${value}`}
  >
    {label}
  </TableSortLabel>
);

const Count: FC<{ value: number; color?: string }> = ({ value, color }) => (
  <TableCell
    align="right"
    sx={{
      fontVariantNumeric: "tabular-nums",
      color: value && color ? color : undefined,
      fontWeight: value && color === "warning.dark" ? 600 : undefined,
    }}
  >
    {value}
  </TableCell>
);

/**
 * The programs as a table, one row for each, in the order given. A click on a sortable header picks
 * that sort. The Access column shows only with the access data, which only admins can read.
 */
export const ProgramTable: FC<{
  matches: ProgramMatch[];
  tree: ProgramTree;
  access?: AccessData;
  sort: ProgramSort;
  onSort: (sort: ProgramSort) => void;
  today: Date;
  onToggleFavourite: (match: ProgramMatch) => void;
  /** After a move from a row's More actions menu, which shows only with this, for admins. */
  onMoved?: () => void;
}> = ({ matches, tree, access, sort, onSort, today, onToggleFavourite, onMoved }) => {
  const shown = columns.filter(({ label }) => access || label !== "Access");

  return (
    <Paper variant="outlined" sx={{ overflowX: "auto", borderRadius: 2 }}>
      <Table aria-label="Programs" sx={{ minWidth: (access ? 1120 : 970) + (onMoved ? 56 : 0) }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headSx, width: 56 }}>
              <Box component="span" sx={visuallyHidden}>
                Favourite
              </Box>
            </TableCell>
            <TableCell sx={headSx} sortDirection={sort === "name" ? "asc" : false}>
              <SortHeader
                label="Program"
                value="name"
                direction="asc"
                sort={sort}
                onSort={onSort}
              />
            </TableCell>
            {shown.map((column) => (
              <TableCell
                key={column.label}
                align={column.numeric ? "right" : "left"}
                sortDirection={column.sort && sort === column.sort ? column.direction : false}
                sx={{ ...headSx, width: column.width }}
              >
                {column.sort && column.direction ? (
                  <SortHeader
                    label={column.label}
                    value={column.sort}
                    direction={column.direction}
                    sort={sort}
                    onSort={onSort}
                  />
                ) : (
                  column.label
                )}
              </TableCell>
            ))}
            {onMoved ? (
              <TableCell sx={{ ...headSx, width: 56 }}>
                <Box component="span" sx={visuallyHidden}>
                  Actions
                </Box>
              </TableCell>
            ) : null}
          </TableRow>
        </TableHead>
        <TableBody>
          {matches.map((match) => {
            const { summary, folders } = match;
            const { node } = summary;
            const next = describeNext(summary);

            return (
              <TableRow
                key={node.id}
                hover
                sx={{ position: "relative", "&:hover .program-title": { color: "primary.dark" } }}
              >
                <TableCell sx={{ pl: 1.5, pr: 0 }}>
                  <FavouriteButton
                    node={node}
                    favourite={summary.favourite}
                    onToggle={() => onToggleFavourite(match)}
                  />
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", minWidth: 0 }}>
                    <ProgramBadge node={node} size={36} />
                    <Stack sx={{ minWidth: 0 }}>
                      <Typography
                        variant="subtitle2"
                        component="span"
                        sx={{ overflowWrap: "anywhere" }}
                      >
                        <ProgramTitleLink node={node} />
                      </Typography>
                      {folders.length ? (
                        <Box sx={{ mt: 0.5 }}>
                          <MatchBadge folders={folders} />
                        </Box>
                      ) : null}
                    </Stack>
                  </Stack>
                </TableCell>
                <Count value={summary.folders.length} />
                <Count value={summary.communications} />
                <Count value={summary.active} color="success.dark" />
                <Count value={summary.drafts} color="warning.dark" />
                <TableCell>
                  <Stack sx={{ maxWidth: 260 }}>
                    <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                      {next.short}
                    </Typography>
                    <Typography variant="caption" noWrap sx={{ color: "text.secondary" }}>
                      {next.detail}
                    </Typography>
                  </Stack>
                </TableCell>
                {access ? (
                  <TableCell>
                    <AccessPill data={access} tree={tree} node={node} avatars={false} />
                  </TableCell>
                ) : null}
                <TableCell sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                  {updatedShortText(summary, today) ?? "—"}
                </TableCell>
                {onMoved ? (
                  <TableCell sx={{ pl: 0, pr: 1.5 }}>
                    <Box sx={ABOVE_LINK}>
                      <ProgramActionsMenu
                        tree={tree}
                        program={node}
                        onMoved={onMoved}
                        label={`More actions for ${node.title}`}
                        analyticsPrefix="programs-landing"
                      />
                    </Box>
                  </TableCell>
                ) : null}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
};
